import { useState } from "react";
import type { SyncStatus } from "../hooks/useStore";
import type { Account } from "../save/store";

interface Props {
  account: Account | null;
  available: boolean;
  status: SyncStatus;
  onSignIn: () => void;
  onSyncNow: () => void;
  onSignedOut: () => void;
  onFamilyDeleted: () => void;
  onPrivacy: () => void;
  setNotice: (text: string) => void;
}

function ago(iso: string | null): string {
  if (!iso) return "not yet";
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

export function syncLine(status: SyncStatus): string {
  if (status.syncing) return "Syncing…";
  if (status.error?.kind === "auth") return "Signed out on the server. Sign in again to sync.";
  const waiting = status.pending
    ? ` ${status.pending} change${status.pending === 1 ? "" : "s"} waiting to upload.`
    : "";
  if (status.error) return `Couldn't sync, will try again.${waiting}`;
  return `Synced ${ago(status.lastSync)}.${waiting}`;
}

async function saveJson(obj: object, filename: string) {
  const file = new File([JSON.stringify(obj, null, 2)], filename, { type: "application/json" });
  try {
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: "Nine Scrolls family data" });
      return;
    }
  } catch (err) {
    if ((err as Error).name === "AbortError") return;
  }
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Family account: sign in, sync status, export, sign out, delete. */
export function AccountPanel({
  account,
  available,
  status,
  onSignIn,
  onSyncNow,
  onSignedOut,
  onFamilyDeleted,
  onPrivacy,
  setNotice,
}: Props) {
  const [signOutArmed, setSignOutArmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!available) return null;

  if (!account)
    return (
      <div className="panel">
        <span className="panel-label">Family account</span>
        <p className="hint">
          Sign in to keep progress safe and share it between devices, with a profile for each child.
          Playing without an account keeps working as it does now.
        </p>
        <div className="transfer-row">
          <button type="button" className="mini" onClick={onSignIn}>
            Sign in or create an account
          </button>
          <button type="button" className="mini" onClick={onPrivacy}>
            Privacy
          </button>
        </div>
      </div>
    );

  const doExport = async () => {
    setBusy(true);
    try {
      const { exportFamily } = await import("../cloud/api");
      await saveJson(
        await exportFamily(),
        `nine-scrolls-family-${new Date().toISOString().slice(0, 10)}.json`,
      );
      setNotice("Family data exported.");
    } catch {
      setNotice("Couldn't export. Check the internet and try again.");
    } finally {
      setBusy(false);
    }
  };

  const doSignOut = async () => {
    if (status.pending > 0 && !signOutArmed) {
      setSignOutArmed(true);
      return;
    }
    setBusy(true);
    try {
      const { signOut } = await import("../cloud/api");
      await signOut();
    } catch {
      /* signing out locally still works */
    }
    setBusy(false);
    onSignedOut();
  };

  const doDelete = async () => {
    if (confirm.trim().toLowerCase() !== "delete") return;
    setBusy(true);
    try {
      const { deleteFamily } = await import("../cloud/api");
      await deleteFamily();
      onFamilyDeleted();
    } catch {
      setNotice("Couldn't delete. Check the internet and try again.");
      setBusy(false);
    }
  };

  return (
    <div className="panel">
      <span className="panel-label">Family account</span>
      <p className="hint">
        Signed in as <b>{account.email}</b>. {syncLine(status)}
      </p>
      <div className="transfer-row">
        <button type="button" className="mini" onClick={onSyncNow} disabled={status.syncing}>
          Sync now
        </button>
        <button type="button" className="mini" onClick={() => void doExport()} disabled={busy}>
          Export everything
        </button>
        <button type="button" className="mini" onClick={() => void doSignOut()} disabled={busy}>
          {signOutArmed ? "Sign out anyway" : "Sign out"}
        </button>
        <button type="button" className="mini" onClick={onPrivacy}>
          Privacy
        </button>
      </div>
      {signOutArmed && (
        <p className="hint">
          {status.pending} change{status.pending === 1 ? " hasn't" : "s haven't"} uploaded yet and
          will be lost if you sign out now. Connect to the internet and tap Sync now first.
        </p>
      )}
      {!deleting ? (
        <button type="button" className="danger" onClick={() => setDeleting(true)}>
          Delete our family account
        </button>
      ) : (
        <div className="paste">
          <p className="hint">
            This deletes every child, all their progress and your login, on every device. It
            can&apos;t be undone. Type <b>delete</b> to confirm.
          </p>
          <label htmlFor="delConfirm" className="visually-hidden">
            Type delete to confirm
          </label>
          <input
            id="delConfirm"
            type="text"
            autoComplete="off"
            autoCapitalize="none"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          <div className="transfer-row">
            <button
              type="button"
              className="danger"
              disabled={busy || confirm.trim().toLowerCase() !== "delete"}
              onClick={() => void doDelete()}
            >
              Delete everything
            </button>
            <button type="button" className="mini" onClick={() => setDeleting(false)}>
              Keep our account
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
